import type { ApolloClient, RefetchQueriesInclude } from '@apollo/client';
import { GetAggregateData } from '@frameio/collections/gql/generated/GetAggregateData';
import {
  GetCollectionAssets,
  type GetCollectionAssetsQuery,
  type GetCollectionAssetsQueryVariables,
} from '@frameio/collections/gql/generated/GetCollectionAssets';
import type { FinderRefetchOptions } from '@frameio/shared/contexts/FinderRefetchContext';
import { getIsOrderPossiblyChanged } from './getIsOrderPossiblyChanged';
import type { GetCollectionAssetsObservableQuery } from './shared';

/**
 * getRefetchCollectionAssetsOptions
 *
 * This utility function returns refetch query options for Finder 2 assets.
 *
 * @param {ApolloClient} client  Apollo client
 * @param {FinderRefetchOptions} opts Refetch options
 * @returns {RefetchQueriesOptions}
 */
export const getRefetchCollectionAssetsOptions = (
  client: ApolloClient<any>,
  observedQueryNames: Set<string>,
  opts?: FinderRefetchOptions
) => {
  const include: RefetchQueriesInclude = [];
  observedQueryNames.forEach((queryName) => {
    if (queryName === 'GetCollectionAssets') {
      include.push(GetCollectionAssets);
      include.push(GetAggregateData);
    }
  });
  return {
    include,
    onQueryUpdated: async (query: GetCollectionAssetsObservableQuery) => {
      if (
        opts?.changedFieldDefinitionIds &&
        !getIsOrderPossiblyChanged(query, opts.changedFieldDefinitionIds)
      ) {
        return false;
      }

      if (
        opts?.onlyRefetchForGroups &&
        !query.variables?.query.groupBy?.length
      ) {
        return false;
      }

      const collectionAssets = query.getCurrentResult().data.collectionAssets;
      const fetchedNodes = collectionAssets?.nodes ?? [];
      const fetchedGroups = collectionAssets?.groups;

      const refetchedNodes: NonNullable<
        GetCollectionAssetsQuery['collectionAssets']
      >['nodes'] = [];

      const lastLoadedGroupId = fetchedGroups?.length
        ? fetchedGroups[fetchedGroups.length - 1].id
        : undefined;
      const lastLoadedGroupCount = lastLoadedGroupId
        ? fetchedNodes.filter(
            (node: any) => node.group?.id === lastLoadedGroupId
          ).length
        : undefined;

      const shouldContinueFetching = (hasNextPage?: boolean) => {
        if (lastLoadedGroupId && lastLoadedGroupCount !== undefined) {
          //if there are groups, we need to fetch until we have the last loaded group
          return (
            hasNextPage &&
            fetchedNodes.filter(
              (node: any) => node.group?.id === lastLoadedGroupId
            ).length < lastLoadedGroupCount
          );
        }
        //if there aren't groups we need to fetch until we have at least the number of nodes we started with
        return hasNextPage && refetchedNodes.length < fetchedNodes?.length;
      };

      const fetchNextPage = async (after: string | null = null) => {
        return client.query<
          GetCollectionAssetsQuery,
          GetCollectionAssetsQueryVariables
        >({
          query: query.query,
          fetchPolicy: 'network-only',
          variables: {
            ...query.variables!,
            page: {
              first: 1000,
              after,
            },
          },
        });
      };

      const fetchSequentially = async (after: string | null = null) => {
        const { data } = await fetchNextPage(after);
        if (data.collectionAssets?.nodes)
          refetchedNodes.push(...data.collectionAssets?.nodes);
        else return;
        const pageInfo = data.collectionAssets?.pageInfo;
        if (shouldContinueFetching(pageInfo?.hasNextPage ?? false)) {
          await fetchSequentially(pageInfo.endCursor);
        }
      };

      fetchSequentially();
      return false;
    },
  };
};
